import React, { useState } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Wallet,
  Star,
  MoreVertical,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { PageLoader } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useTranslation } from '@/i18n';
import {
  usePots,
  useCreatePot,
  useUpdatePot,
  useDeletePot,
  useSetDefaultPot,
} from '@/hooks/usePots';
import type { Pot, CreatePotInput, UpdatePotInput } from '@/types/pot';
import { POT_COLORS, CURRENCY_SYMBOLS, CURRENCY_NAMES } from '@/types/pot';

const formatCurrency = (value: number | undefined | null, currency: string, locale: string) => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const numValue = typeof value === 'number' ? value : parseFloat(String(value)) || 0;
  return `${symbol} ${numValue.toLocaleString(locale, { minimumFractionDigits: 2 })}`;
};

const currencyOptions = Object.entries(CURRENCY_NAMES).map(([value, label]) => ({
  value,
  label: `${CURRENCY_SYMBOLS[value]} - ${label}`,
}));

export const PotsPage: React.FC = () => {
  const { t, locale } = useTranslation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPot, setEditingPot] = useState<Pot | null>(null);
  const [deletingPot, setDeletingPot] = useState<Pot | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCurrency, setFormCurrency] = useState<'BRL' | 'GBP' | 'USD' | 'EUR'>('BRL');
  const [formColor, setFormColor] = useState<string>(POT_COLORS[0]);

  const { data, isLoading, isError } = usePots();
  const createMutation = useCreatePot();
  const updateMutation = useUpdatePot();
  const deleteMutation = useDeletePot();
  const setDefaultMutation = useSetDefaultPot();

  const pots = data?.pots || [];
  const totalBalance = data?.totalBalance || {};

  const handleCreate = () => {
    setEditingPot(null);
    setFormName('');
    setFormDescription('');
    setFormCurrency('BRL');
    setFormColor(POT_COLORS[0]);
    setIsFormOpen(true);
  };

  const handleEdit = (pot: Pot) => {
    setEditingPot(pot);
    setFormName(pot.name);
    setFormDescription(pot.description || '');
    setFormCurrency(pot.currency);
    setFormColor(pot.color);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingPot) {
      const updates: UpdatePotInput = {};
      if (formName !== editingPot.name) updates.name = formName;
      if (formDescription !== (editingPot.description || '')) updates.description = formDescription;
      if (formColor !== editingPot.color) updates.color = formColor;

      if (Object.keys(updates).length > 0) {
        await updateMutation.mutateAsync({ id: editingPot.id, data: updates });
      }
    } else {
      const newPot: CreatePotInput = {
        name: formName,
        description: formDescription || undefined,
        currency: formCurrency,
        color: formColor,
      };
      await createMutation.mutateAsync(newPot);
    }
    setIsFormOpen(false);
    setEditingPot(null);
  };

  const handleDelete = async () => {
    if (deletingPot) {
      await deleteMutation.mutateAsync(deletingPot.id);
      setDeletingPot(null);
    }
  };

  const handleSetDefault = async (potId: string) => {
    await setDefaultMutation.mutateAsync(potId);
  };

  if (isLoading) {
    return <PageLoader message={t.app.pots.loading} />;
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {t.app.pots.errorTitle}
        </h2>
        <p className="text-gray-500 dark:text-gray-400">
          {t.app.pots.error}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t.app.pots.title}</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t.app.pots.subtitle}
          </p>
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} onClick={handleCreate}>
          {t.app.pots.newPot}
        </Button>
      </div>

      {/* Total balances by currency */}
      {Object.keys(totalBalance).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(totalBalance).map(([currency, balance]) => (
            <Card key={currency} className="bg-gradient-to-br from-primary-50 to-primary-100 dark:from-primary-900/20 dark:to-primary-800/20">
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400">{CURRENCY_NAMES[currency]}</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(balance as number, currency, locale)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pots grid */}
      {pots.length === 0 ? (
        <Card className="p-8 text-center">
          <Wallet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {t.app.pots.noPots}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-sm mx-auto mb-4">
            {t.app.pots.noPotsDescription}
          </p>
          <Button onClick={handleCreate} leftIcon={<Plus className="w-4 h-4" />}>
            {t.app.pots.createFirst}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pots.map((pot) => (
            <Card key={pot.id} className="group hover:shadow-md transition-shadow">
              <CardContent>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: `${pot.color}20`,
                        color: pot.color,
                      }}
                    >
                      <Wallet className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {pot.name}
                        </p>
                        {pot.isDefault && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                      {pot.description && (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {pot.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="relative group/menu">
                    <button className="p-1 rounded hover:bg-gray-100 dark:hover:bg-secondary-700 opacity-0 group-hover:opacity-100 transition-opacity">
                      <MoreVertical className="w-4 h-4 text-gray-500" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-secondary-700 py-1 z-10 hidden group-hover/menu:block">
                      {!pot.isDefault && (
                        <button
                          onClick={() => handleSetDefault(pot.id)}
                          className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 flex items-center gap-2"
                        >
                          <Star className="w-4 h-4" />
                          {t.app.pots.setDefault}
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(pot)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 flex items-center gap-2"
                      >
                        <Pencil className="w-4 h-4" />
                        {t.app.common.edit}
                      </button>
                      <button
                        onClick={() => setDeletingPot(pot)}
                        className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        {t.app.common.delete}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(pot.balance, pot.currency, locale)}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" size="sm">
                      {pot.currency}
                    </Badge>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {pot.transactionCount} {t.app.pots.transactions}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false);
          setEditingPot(null);
        }}
        title={editingPot ? t.app.pots.editPot : t.app.pots.newPot}
        size="md"
      >
        <form onSubmit={handleFormSubmit} className="space-y-4">
          <Input
            label={t.app.pots.form.name}
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder={t.app.pots.form.namePlaceholder}
            required
          />

          <Input
            label={t.app.pots.form.description}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            placeholder={t.app.pots.form.descriptionPlaceholder}
          />

          {!editingPot && (
            <Select
              label={t.app.pots.form.currency}
              value={formCurrency}
              onChange={(e) => setFormCurrency(e.target.value as 'BRL' | 'GBP' | 'USD' | 'EUR')}
              options={currencyOptions}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t.app.pots.form.color}
            </label>
            <div className="flex flex-wrap gap-2">
              {POT_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setFormColor(color)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    formColor === color
                      ? 'border-gray-900 dark:border-white scale-110'
                      : 'border-transparent hover:scale-105'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsFormOpen(false);
                setEditingPot(null);
              }}
            >
              {t.app.common.cancel}
            </Button>
            <Button
              type="submit"
              isLoading={createMutation.isPending || updateMutation.isPending}
            >
              {editingPot ? t.app.common.save : t.app.common.create}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingPot}
        onClose={() => setDeletingPot(null)}
        title={t.app.pots.confirmDelete}
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            {t.app.pots.confirmDeleteMessage}{' '}
            <span className="font-semibold text-gray-900 dark:text-white">
              {deletingPot?.name}
            </span>
            ?
          </p>
          {deletingPot && deletingPot.transactionCount > 0 && (
            <p className="text-sm text-yellow-600 dark:text-yellow-500">
              {t.app.pots.deleteWarning.replace('{count}', String(deletingPot.transactionCount))}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setDeletingPot(null)}>
              {t.app.common.cancel}
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              isLoading={deleteMutation.isPending}
            >
              {t.app.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PotsPage;
